import { useEffect, useRef, useState } from 'react';
import { Brand, Logo } from '../../Logo.js';
import { LogoutButton } from '../../LogoutButton.js';
import { PrivacyModal, AcceptanceStatus, PRIVACY_VERSION, type PrivacyAcceptance } from './PrivacyStatement.js';
import content from './content.json';
import './intro.css';
const labels = ['An introduction', 'What Cobrain is', 'What it does', 'How it works', 'Deals and companies', 'Intelligence all round', 'Privacy rails', 'Where it is going'];
const t = (page: number, index: number) => content[page - 1]?.text[index] ?? '';

function Arrow({ back = false }: { back?: boolean }) {
  return <svg className={`arrow ${back ? 'back-arrow' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M4 12h15m-6-6 6 6-6 6" /></svg>;
}
function Tick() {
  return <svg className="tick" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>;
}
function Lock() {
  return <svg className="lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><rect x="5" y="10" width="14" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3m-4 5v3" /></svg>;
}
function Heading({ page, intro }: { page: number; intro?: number }) {
  return <header className="chapter-heading">
    <div className="eyebrow"><span className="chapter-number">{String(page).padStart(2, '0')}</span>{labels[page - 1]}</div>
    <h1 id="chapter-title" tabIndex={-1}>{t(page, 1)}</h1>
    {intro !== undefined && <p className="chapter-intro">{t(page, intro)}</p>}
  </header>;
}
function Pairs({ page, indices, className = '' }: { page: number; indices: number[]; className?: string }) {
  return <div className={`pairs ${className}`}>{indices.map(i => <section key={i}><h3>{t(page, i)}</h3><p>{t(page, i + 1)}</p></section>)}</div>;
}
function Quote({ page, quote, caption }: { page: number; quote: number; caption?: number }) {
  return <figure className="quote"><span className="quote-mark" aria-hidden="true">“</span><div><blockquote>{t(page, quote)}</blockquote>{caption !== undefined && <figcaption>{t(page, caption)}</figcaption>}</div></figure>;
}

function Cover() {
  return <>
    <div className="cover-grid">
      <div className="cover-story">
        <div className="eyebrow"><span className="chapter-number">01</span>{t(1, 1)}</div>
        <h1 id="chapter-title" tabIndex={-1}><span>{t(1, 2)}</span><span>{t(1, 3)}</span></h1>
        <p className="cover-intro">{t(1, 4)}</p>
        <div className="principles">{[5, 6, 7].map((i, j) => <span key={i} className={j === 1 ? 'privacy-principle' : ''}>{j === 1 ? <Lock /> : <Tick />}{t(1, i)}</span>)}</div>
      </div>
      <section className="answers">
        <div className="answers-head"><span>{t(1, 8)}</span><Logo size={38} /></div>
        {[9, 11, 13, 15].map((i, n) => <div className="answer" key={i}><span className="answer-index">{String(n + 1).padStart(2, '0')}</span><div><h2>{t(1, i)}</h2><p>{t(1, i + 1)}</p></div><span className="answer-arrow" aria-hidden="true">↗</span></div>)}
      </section>
    </div>
    <section className="inside"><h2>{t(1, 17)}</h2><ol>{[19, 21, 23, 25, 27, 29, 31].map((i, n) => <li key={i}><span>{t(1, i - 1)}</span>{t(1, i)}</li>)}</ol></section>
  </>;
}

function WhatItIs() {
  return <>
    <Heading page={2} />
    <div className="understanding-grid">
      <div className="problem"><h2>{t(2, 2)}</h2><p>{t(2, 3)}</p><div className="comparison"><section><h3>{t(2, 16)}</h3><p>{t(2, 17)}</p></section><section><h3>{t(2, 18)}</h3><p>{t(2, 19)}</p></section></div></div>
      <section className="signal-map" aria-label="Conversations become answers">
        <div className="signal-inputs">{[4, 5, 6, 7, 8, 9].map((i, n) => <div key={i}><span className="signal-marker" aria-hidden="true">{['@', '“', '↗', '▦', '≡', '↗'][n]}</span>{t(2, i)}</div>)}</div>
        <div className="signal-merge" aria-hidden="true"><span/><span/><span/></div>
        <div className="signal-brain"><Brand size={42}/><p>{t(2, 11)}</p></div>
        <div className="signal-stem" aria-hidden="true" />
        <div className="signal-outputs">{[12, 13, 14, 15].map(i => <div key={i}><Tick />{t(2, i)}</div>)}</div>
      </section>
    </div>
    <Quote page={2} quote={21} caption={22}/>
    <Pairs page={2} indices={[23, 25, 27]} className="three" />
  </>;
}

function WhatItDoes() {
  return <>
    <Heading page={3} intro={2} />
    <div className="decision-layout">
      <section className="decision-example" aria-label="Illustrative triage card">
        <div className="example-top"><span>{t(3, 3)}</span><span className="clock-label">{t(3, 4)}</span></div>
        <div className="example-content"><span className="decision-rank" aria-hidden="true">01</span><h2>{t(3, 5)}</h2><p className="evidence"><strong>{t(3, 6)}</strong> {t(3, 7)}</p>
          <div className="example-options">{[8, 9, 10].map(i => <span key={i} className="example-option">{t(3, i)}<Arrow /></span>)}</div>
          <p className="example-note">{t(3, 11)}</p>
        </div>
      </section>
      <Pairs page={3} indices={[12, 14, 16, 18, 20, 22]} className="capabilities" />
    </div>
    <Pairs page={3} indices={[24, 26, 28]} className="three assurance" />
  </>;
}

function HowItWorks() {
  return <>
    <Heading page={4} />
    <div className="process-layout">
      <div><ol className="process">{[3, 6, 9, 12, 15].map((i, n) => <li key={i}><span className={`process-number ${n === 2 ? 'private-number' : ''}`}>{t(4, i - 1)}</span><div><h2>{t(4, i)}</h2><p>{t(4, i + 1)}</p></div></li>)}</ol><div className="process-outcome"><Tick/><strong>{t(4, 17)}</strong></div></div>
      <aside className="brain-notes"><div className="brain-mark"><Logo size={70}/><span aria-hidden="true">↔</span><Logo size={42}/></div><h2>{t(4, 18)}</h2><Pairs page={4} indices={[19, 21, 23]}/><div className="corrections"><strong>{t(4, 25)}</strong><p>{t(4, 26)}</p></div></aside>
    </div>
  </>;
}

function Deals() {
  return <>
    <Heading page={5} intro={2} />
    <div className="deals-grid">
      <section><div className="section-index">01</div><h2>{t(5, 3)}</h2><p>{t(5, 4)}</p><div className="deal-example"><span className="small-label">{t(5, 5)}</span><h3>{t(5, 6)}</h3><p className="deal-quote">{t(5, 7)}</p><div className="example-choices"><span><Tick/>{t(5, 8)}</span><span>{t(5, 9)}</span></div></div></section>
      <section><div className="section-index">02</div><h2>{t(5, 10)}</h2><p>{t(5, 11)}</p><div className="deal-example"><span className="small-label">{t(5, 12)}</span><div className="keen-heading"><h3>{t(5, 13)}</h3><strong>{t(5, 14)}</strong></div><div className="keen-scale" aria-hidden="true">{[1, 2, 3, 4, 5].map(n => <span key={n} className={`lv-kb k${n} keen-${n} ${n === 4 ? 'keen-now' : ''}`}>{n}</span>)}</div><p>{t(5, 15)} <span className="text-undo">{t(5, 16)}</span></p></div></section>
    </div>
    <Pairs page={5} indices={[17, 19, 21]} className="three assurance compact" />
    <section className="company-section"><h2>{t(5, 23)}</h2><div className="company-grid"><Pairs page={5} indices={[24, 26]}/><div className="company-core"><span className="company-symbol" aria-hidden="true">▦</span><h3>{t(5, 28)}</h3><p>{t(5, 29)}</p></div><Pairs page={5} indices={[30, 32]}/></div><p className="privacy-note"><Lock/>{t(5, 34)}</p></section>
  </>;
}

function Intelligence() {
  return <>
    <Heading page={6}/>
    <div className="intelligence-metrics">{[2, 4, 6].map(i => <div key={i}><strong>{t(6, i)}</strong><p>{t(6, i + 1)}</p></div>)}</div>
    <Pairs page={6} indices={[8, 10, 12, 14, 16, 18]} className="intelligence-features" />
    <div className="intelligence-bottom"><section className="ask-example"><span className="ask-mark" aria-hidden="true">?</span><div><h2>{t(6, 20)}</h2><p>{t(6, 21)}</p></div></section><section className="joins-up"><h2>{t(6, 22)}</h2><p>{t(6, 23)}</p></section></div>
  </>;
}

function Privacy({ acceptance, onAccept }: { acceptance: PrivacyAcceptance | null; onAccept: () => Promise<PrivacyAcceptance> }) {
  const [open, setOpen] = useState(false);
  return <>
    <div className="privacy-heading"><Heading page={7} intro={2}/><div className="privacy-seal" aria-hidden="true"><div><Lock/></div></div></div>
    <div className="privacy-layout"><ol className="rails">{[4, 7, 10].map(i => <li key={i}><span className="rail-number">{t(7, i - 1)}</span><div><h2>{t(7, i)}</h2><p>{t(7, i + 1)}</p></div></li>)}</ol><Pairs page={7} indices={[15, 17, 19, 21, 23, 25]} className="privacy-details" /></div>
    <Quote page={7} quote={13} caption={14}/>
    <section className="privacy-intro-ack" aria-label="Privacy acceptance"><Lock/><div><h2>Read the full privacy statement.</h2><p>What is read, how it is used, who may access it, how long it is kept, and your rights. Read and accept it before connecting your mailbox.</p>{acceptance && <AcceptanceStatus acceptance={acceptance}/>}<button type="button" className={acceptance ? 'setup-secondary' : 'primary'} onClick={() => setOpen(true)}>{acceptance ? 'Read the statement again' : 'Read privacy statement'}<Arrow/></button></div></section>
    {open && <PrivacyModal acceptance={acceptance} onAccept={onAccept} onClose={() => setOpen(false)}/>}
  </>;
}

function Future() {
  return <>
    <Heading page={8}/>
    <div className="future-grid">{[2, 5, 8].map((i, n) => <section key={i}><span className="future-type"><span aria-hidden="true">{['↙', '=', '↗'][n]}</span>{t(8, i)}</span><h2>{t(8, i + 1)}</h2><p>{t(8, i + 2)}</p></section>)}</div>
    <section className="vision"><div><span className="small-label">{t(8, 11)}</span><h2>{t(8, 12)}</h2><p>{t(8, 13)}</p></div><Logo size={136}/></section>
    <section className="first-day"><h2>{t(8, 14)}</h2><ol>{[16, 19, 22, 25, 28].map(i => <li key={i}><span>{t(8, i - 1)}</span><h3>{t(8, i)}</h3><p>{t(8, i + 1)}</p></li>)}</ol></section>
    <div className="closing-note"><h2>{t(8, 30)}</h2><p>{t(8, 31)}</p></div>
  </>;
}


export function Introduction({ identity, acceptance, onAccept, onComplete }: {
  identity: string; acceptance: PrivacyAcceptance | null;
  onAccept: () => Promise<PrivacyAcceptance>; onComplete: () => void;
}) {
  const key = `onboarding-introduction:${identity}:${PRIVACY_VERSION}`;
  const accepted = acceptance?.version === PRIVACY_VERSION;
  const [progress, setProgress] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(key) ?? 'null');
      const visited = Number.isInteger(saved?.visited) ? Math.max(1, Math.min(accepted ? 8 : 7, saved.visited)) : (accepted ? 7 : 1);
      const page = Number.isInteger(saved?.page) ? Math.max(1, Math.min(visited, saved.page)) : (accepted ? 7 : 1);
      return { page, visited };
    } catch { return { page: 1, visited: 1 }; }
  });
  const { page, visited } = progress;
  const firstRender = useRef(true);
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(progress)); } catch {} }, [key, progress]);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.getElementById('chapter-title')?.focus({ preventScroll: true });
  }, [page]);
  const go = (next: number) => {
    if (next < 1 || next > 9 || (next > 7 && !accepted)) return;
    if (next === 9) { onComplete(); return; }
    setProgress(current => ({ page: next, visited: Math.max(current.visited, next) }));
  };
  const canNext = page < 7 || accepted;
  return <div className={`mc intro-app ${page === 7 ? 'privacy-chapter' : ''}`}>
    <header className="app-header"><Brand size={42}/><div className="header-context"><span>An introduction</span><span className="header-divider"/><span>iGame Media</span></div><LogoutButton /></header>
    <div className="walkthrough-body">
      <aside className="chapter-rail" aria-label="Introduction chapters"><span className="rail-line" aria-hidden="true"/>{labels.map((label, i) => <button key={label} type="button" aria-label={`${i + 1}: ${label}`} aria-current={page === i + 1 ? 'step' : undefined} disabled={i + 1 > visited} onClick={() => go(i + 1)}>{String(i + 1).padStart(2, '0')}{i + 1 < visited && page !== i + 1 && <span className="visited-dot"/>}</button>)}</aside>
      <main key={page} className={`chapter chapter-${page}`} aria-labelledby="chapter-title">
        {page === 1 && <Cover/>}{page === 2 && <WhatItIs/>}{page === 3 && <WhatItDoes/>}{page === 4 && <HowItWorks/>}{page === 5 && <Deals/>}{page === 6 && <Intelligence/>}{page === 7 && <Privacy acceptance={acceptance} onAccept={onAccept}/>} {page === 8 && <Future/>}
        {page <= 8 && <p className="source-footer">{content[page - 1]?.text.at(-2)}</p>}
      </main>
    </div>
    <footer className="walkthrough-footer"><button type="button" className="back-button" disabled={page === 1} onClick={() => go(page - 1)}><Arrow back/>Back</button>
      <div className="progress-group"><ol className="dots" aria-label="Your position">{labels.map((label, i) => <li key={label}><span className={`${page === i + 1 ? 'current' : ''} ${i + 1 < page ? 'complete' : ''}`} aria-current={page === i + 1 ? 'step' : undefined}><span className="sr-only">{i + 1}: {label}</span></span></li>)}</ol><span className="page-counter" aria-live="polite">{page <= 8 ? `${String(page).padStart(2, '0')} / 08` : 'Introduction complete'}</span></div>
      {page <= 8 ? <div className="next-group"><span className="next-label">{page < 8 ? labels[page] : 'Connect with Microsoft'}</span><button type="button" className="primary next-button" disabled={!canNext} aria-describedby={!canNext ? 'privacy-required' : undefined} onClick={() => go(page + 1)}>Next<Arrow/></button></div> : <span className="preview-end">End of preview</span>}
      {!canNext && <span id="privacy-required" className="footer-reason">Read and accept the privacy statement to continue.</span>}
    </footer>
  </div>;
}
